import { useTranslation } from 'react-i18next';
import { ChevronDown, Funnel } from 'lucide-react';

import type { ListFiltersProps } from './types';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';

import { cn } from '@/lib/utils';

import { ListFilterChips } from './ListFilterChips';
import { ListFilterField } from './ListFilterField';
import { ListFiltersActions } from './ListFiltersActions';
import { useListFiltersSummary } from './useListFiltersSummary';

export const ListFilters = (props: ListFiltersProps) => {
  const {
    filters,
    defaultOpen = false,
    className,
    gridClassName,
    onApplyFilters,
    hasChanges = false,
  } = props;
  const { t } = useTranslation();
  const { activeChips, activeFiltersCount, isClearable, onAllFiltersClear } =
    useListFiltersSummary(filters);

  if (filters.length === 0) {
    return null;
  }

  return (
    <Collapsible
      defaultOpen={defaultOpen}
      className={cn(
        'group/filters bg-card w-full rounded-lg border',
        className,
      )}
    >
      <div className="flex min-h-12 items-center gap-2 px-3 py-2">
        <CollapsibleTrigger asChild>
          <Button
            variant="outline"
            className="group-data-[state=open]/filters:bg-accent relative shrink-0"
          >
            <Funnel
              className={cn(
                activeFiltersCount > 0 ? 'text-link' : 'text-muted-foreground',
              )}
            />
            {t('Filters')}
            {activeFiltersCount > 0 && (
              <Badge className="aspect-square h-auto min-w-4.5 rounded-full px-1">
                {activeFiltersCount}
              </Badge>
            )}
            <ChevronDown className="text-muted-foreground transition-transform duration-200 group-data-[state=open]/filters:rotate-180" />
            {hasChanges && (
              <span
                aria-hidden="true"
                className="bg-warning ring-card absolute -top-1 -right-1 hidden size-2 rounded-full ring-2 group-data-[state=closed]/filters:block"
              />
            )}
          </Button>
        </CollapsibleTrigger>
        <ListFilterChips chips={activeChips} />
      </div>
      <CollapsibleContent className="data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down overflow-hidden">
        <div className="bg-card border-t p-3">
          <div
            className={cn(
              'grid grid-cols-1 items-start gap-3 sm:grid-cols-2 md:grid-cols-3 **:data-[slot=input-label]:text-xs **:data-[slot=input-label]:text-muted-foreground',
              gridClassName,
            )}
          >
            {filters.map((filter) => (
              <ListFilterField
                key={filter.name}
                filter={filter}
              />
            ))}
          </div>
          {onApplyFilters && (
            <ListFiltersActions
              hasChanges={hasChanges}
              isClearable={isClearable}
              onAllFiltersClear={onAllFiltersClear}
              onApplyFilters={onApplyFilters}
            />
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};
